import axios from 'axios';

/**
 * Get API Base URL
 * 
 * PRODUCTION (Vercel):
 * - Uses relative paths (/api/...)
 * - Vercel rewrites proxy to Railway backend
 * 
 * DEVELOPMENT:
 * - Uses absolute URLs (http://localhost:8000 or LAN IP)
 */
const getApiBaseUrl = () => {
  // Production: Use relative paths for Vercel proxy
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // Localhost development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Axios] Using localhost');
      }
      return 'http://localhost:8000';
    }

    // LAN/Mobile testing
    if (hostname.match(/^192\.168\.\d+\.\d+$/) || hostname.match(/^10\.\d+\.\d+\.\d+$/)) {
      const url = `http://${hostname}:8000`;
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Axios] Using LAN:', url);
      }
      return url;
    }

    // Production (Vercel) - use relative paths
    // Vercel rewrites will proxy /api/* to Railway backend
    if (process.env.NODE_ENV !== 'production') {
      console.log('[Axios] Using Vercel proxy (relative paths)');
    }
    return '';
  }

  // Fallback for SSR/build time
  return '';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth and CSRF tokens
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token if available
    const csrfToken = localStorage.getItem('csrf_token');
    if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and CSRF token update
api.interceptors.response.use(
  (response) => {
    // Update CSRF token from response header if present
    const newCsrfToken = response.headers['x-csrf-token'];
    if (newCsrfToken) {
      localStorage.setItem('csrf_token', newCsrfToken);
    }
    return response;
  },
  (error) => {
    if (error.response) {
      const status = error.response.status;

      // Update CSRF token from error response header if present (e.g. after 403)
      const newCsrfToken = error.response.headers['x-csrf-token'];
      if (newCsrfToken) {
        localStorage.setItem('csrf_token', newCsrfToken);
      }

      if (status === 401) {
        // Handle unauthorized
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else if (status === 403) {
        // Forbidden - insufficient permissions or CSRF error
        console.error('[API] Forbidden 403:', error.config?.url, error.response.data);
        // If CSRF error, maybe retry? For now just log.
      } else if (status === 404) {
        // Resource not found
        console.error('[API] Not found 404:', error.config?.url, error.response.data);
      } else if (status === 500) {
        // Log server errors for debugging
        console.error('[API] Server error 500:', error.response.data);
      } else if (status === 422) {
        // Validation error - log details
        console.error('[API] Validation error 422:', error.response.data);
      }
    } else if (error.request) {
      // Network error - no response received
      console.error('[API] Network error - no response:', error.message);
    }
    return Promise.reject(error);
  }
);

// Function to fetch initial CSRF token
export const fetchCsrfToken = async () => {
  try {
    const response = await api.get('/api/csrf-token');
    if (response.data?.csrf_token) {
      localStorage.setItem('csrf_token', response.data.csrf_token);
      // console.log('CSRF token initialized');
    }
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
  }
};

// Initialize CSRF token
fetchCsrfToken();

export default api;
